import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Upload, X, Calendar, MapPin } from 'lucide-react';
import { eventsAPI, uploadAPI } from '@/lib/api';

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    id: '',
    title_fa: '',
    description_fa: '',
    event_date: '',
    location: '',
    image_url: '',
    order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await eventsAPI.getAll();
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      setFormData({ ...formData, image_url: response.data.url });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('آپلود تصویر با خطا مواجه شد');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingEvent) {
        await eventsAPI.update(formData.id, formData);
        alert('رویداد با موفقیت به‌روزرسانی شد');
      } else {
        const newId = `event_${Date.now()}`;
        const newEvent = {
          ...formData,
          id: newId,
          order: events.length,
        };
        await eventsAPI.create(newEvent);
        alert('رویداد با موفقیت ایجاد شد');
      }
      
      fetchEvents();
      closeModal();
    } catch (error) {
      console.error('Failed to save event:', error);
      alert('خطا در ذخیره رویداد');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData(event);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این رویداد اطمینان دارید؟')) return;

    try {
      await eventsAPI.delete(id);
      alert('رویداد با موفقیت حذف شد');
      fetchEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('خطا در حذف رویداد');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    setFormData({
      id: '',
      title_fa: '',
      description_fa: '',
      event_date: '',
      location: '',
      image_url: '',
      order: 0,
      is_active: true,
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-black p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">مدیریت رویدادها</h1>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] transition-colors"
          >
            <Plus size={20} />
            افزودن رویداد جدید
          </button>
        </div>

        {/* Events List */}
        <div className="space-y-4">
          {events.map((event) => (
            <div key={event.id} className="glass-effect border border-white/10 rounded-xl p-6 flex gap-6">
              {/* Event Image */}
              {event.image_url && (
                <div className="flex-shrink-0">
                  <img
                    src={event.image_url}
                    alt={event.title_fa}
                    className="w-48 h-32 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Event Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{event.title_fa}</h3>
                    <p className="text-sm text-zinc-400 mb-3">{event.description_fa}</p>
                    <div className="flex gap-4 text-sm text-zinc-500">
                      <div className="flex items-center gap-1">
                        <Calendar size={16} className="text-[#D4AF37]" />
                        <span>{event.event_date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin size={16} className="text-[#D4AF37]" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(event)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(event.id)}
                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mt-3">
                  <span className={`px-3 py-1 rounded-full text-xs ${event.is_active ? 'bg-green-600/20 text-green-400' : 'bg-gray-600/20 text-gray-400'}`}>
                    {event.is_active ? 'فعال' : 'غیرفعال'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {events.length === 0 && (
          <div className="text-center text-zinc-400 py-12">
            هنوز رویدادی ثبت نشده است. برای افزودن رویداد جدید روی دکمه بالا کلیک کنید.
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingEvent ? 'ویرایش رویداد' : 'افزودن رویداد جدید'}
                </h2>
                <button onClick={closeModal} className="text-zinc-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">تصویر رویداد *</label>
                  {formData.image_url ? (
                    <div className="relative">
                      <img src={formData.image_url} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="event-image"
                      />
                      <label htmlFor="event-image" className="cursor-pointer">
                        <Upload className="mx-auto mb-2 text-zinc-400" size={32} />
                        <p className="text-sm text-zinc-400">
                          {uploading ? 'در حال آپلود...' : 'کلیک کنید یا تصویر را بکشید'}
                        </p>
                      </label>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">عنوان رویداد *</label>
                  <input
                    type="text"
                    value={formData.title_fa}
                    onChange={(e) => setFormData({ ...formData, title_fa: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">توضیحات *</label>
                  <textarea
                    value={formData.description_fa}
                    onChange={(e) => setFormData({ ...formData, description_fa: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] min-h-[120px]"
                    required
                  />
                </div>

                {/* Date & Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">تاریخ رویداد *</label>
                    <input
                      type="text"
                      value={formData.event_date}
                      onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                      placeholder="مثال: 15 آذر 1404"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">مکان *</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                      placeholder="مثال: تهران، هتل لاله"
                      required
                    />
                  </div>
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-white/10 bg-black text-[#D4AF37] focus:ring-[#D4AF37]"
                  />
                  <label htmlFor="is_active" className="text-sm text-zinc-300">
                    رویداد فعال است
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] transition-colors font-medium"
                  >
                    {editingEvent ? 'به‌روزرسانی' : 'ایجاد'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEvents;
